import { Box, Button, UnstyledButton } from '@mantine/core';
import { useIntersection } from '@mantine/hooks';
import type { TranslateFn } from '@newo/localization';
import type { CreatorAgentSuccessFlowStepKey as AgentSuccessStep } from '@newo/tenant-metadata';
import { Info } from 'lucide-react';
import { type FC, type MouseEvent, memo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import Title from '@/components/Title';
import VideoPlayer from '@/components/VideoPlayer';
import ScriptClipboard from '@/pages/AgentSuccess/components/StepsBlock/components/StepCard/components/ScriptClipboard';
import { partnerMetadata } from '@/services/PartnerMetadata';
import useAppStore from '@/store/app';
import {
	CardType,
	type TAgentSuccessStepCard
} from '@/types/partner-metadata.types.ts';
import { cn } from '@/utils';

type Props = {
	cardData: TAgentSuccessStepCard;
	isSelected?: boolean;
	isMiniCreatorMode: boolean;
	step: AgentSuccessStep;
	handleActionClick?: (ev: MouseEvent<HTMLButtonElement>) => void;
	onInfoIconClick: (key: AgentSuccessStep) => void;
	translate: TranslateFn;
};

const StepCard: FC<Props> = memo(
	({
		cardData,
		isSelected,
		handleActionClick,
		onInfoIconClick,
		step,
		isMiniCreatorMode,
		translate
	}) => {
		const { ref: stepRef, entry } = useIntersection({
			threshold: 0.6
		});
		const { type, icon: Icon, videoSrc, showInfo } = cardData;

		const { agentData } = useAppStore(
			useShallow((state) => ({
				agentData: state.agentData
			}))
		);

		const scriptValue = agentData?.widget_script || '';

		return (
			<div
				className={cn(
					'flex flex-col border-[var(--color-primary-900)] border md:flex-row overflow-hidden rounded-[15px] p-[24px] gap-4 sm:gap-10 bg-[linear-gradient(0deg,#FFF_0%,#FFF_100%),#FAFAFA]',
					'shadow-[0_24px_48px_rgba(18,18,23,0.03),_0_10px_18px_rgba(18,18,23,0.03),_0_5px_8px_rgba(18,18,23,0.04),_0_2px_4px_rgba(18,18,23,0.04)]',
					{
						'sm:justify-between': !videoSrc,
						'p-2': isMiniCreatorMode
					}
				)}
				data-testid={`agent-success-step-card-${step.toLowerCase()}`}
				ref={stepRef}
			>
				<div
					className={cn('flex flex-col gap-[24px] justify-between', {
						'gap-2 w-full': isMiniCreatorMode,
						'flex-[50%]': !isMiniCreatorMode
					})}
				>
					<Title
						className={cn(
							'text-primary-900 intro-title w-full text-[24px] text-left font-semibold m-0 [font-family:var(--font-headlines)]',
							{
								'xl:!text-[32px]': !isMiniCreatorMode,
								'!text-base': isMiniCreatorMode
							}
						)}
						level={4}
						size="h5"
					>
						{translate(
							'creator',
							`agent_success.next_steps.${step.toLowerCase()}.title`
						)}

						{showInfo && (
							<UnstyledButton
								className="ml-[6px]"
								onClick={() => onInfoIconClick(step)}
							>
								<Info />
							</UnstyledButton>
						)}
					</Title>

					<span className="text-black [font-family:var(--font-body)] text-[12.5px] font-medium leading-[18.5px] tracking-[-0.25px] opacity-50">
						{translate(
							'creator',
							`agent_success.next_steps.${step.toLowerCase()}.subtitle`
						)}
					</span>

					{videoSrc && isMiniCreatorMode && (
						<Box className="overflow-hidden w-full rounded-[8px] bg-black bg-transparent">
							<VideoPlayer
								autoPlay={entry?.isIntersecting}
								className="w-full"
								source={videoSrc}
							/>
						</Box>
					)}

					{type === CardType.SCRIPT && isMiniCreatorMode && (
						<ScriptClipboard
							isMiniCreatorMode={isMiniCreatorMode}
							scriptValue={scriptValue}
						/>
					)}
					<Button
						className={cn(
							'!w-full flex self-center md:self-start items-center justify-center gap-[10px]',
							'rounded-[12px] text-sm font-medium border transition-colors duration-200 [font-family:var(--font-body)]',
							{
								'text-white hover:bg-[var(--button-hover)] hover:text-white':
									isSelected || partnerMetadata.isAlwaysFilledStepButton(),
								'!bg-white !text-black !border-black':
									!isSelected && !partnerMetadata.isAlwaysFilledStepButton(),
								'attention-pop': isSelected && !isMiniCreatorMode,
								'md:max-w-fit ': !isMiniCreatorMode
							}
						)}
						data-card-key={step}
						data-testid={`agent-success-step-action-${step.toLowerCase()}`}
						h={42}
						onClick={handleActionClick}
						py={12}
						style={{
							borderRadius: `${partnerMetadata.getSubmitCtaRadius()}px`
						}}
					>
						{translate(
							'creator',
							`agent_success.next_steps.${step.toLowerCase()}.button_label`
						)}
					</Button>
				</div>
				{videoSrc && !isMiniCreatorMode && (
					<Box className="overflow-hidden w-full rounded-[8px] bg-black flex-[50%] bg-transparent">
						<VideoPlayer
							autoPlay={entry?.isIntersecting}
							className="w-full"
							source={videoSrc}
						/>
					</Box>
				)}

				{type === CardType.SCRIPT && !isMiniCreatorMode && (
					<ScriptClipboard
						isMiniCreatorMode={isMiniCreatorMode}
						scriptValue={scriptValue}
					/>
				)}
				{Icon && !videoSrc && !isMiniCreatorMode && (
					<Icon className="h-[159px] block w-auto opacity-[0.05]" />
				)}
			</div>
		);
	}
);

export default StepCard;
